import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Download, Package, TrendingUp, Rocket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cokeGrades, tradingServices } from '../data/mock';

const Products = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1B4D40] py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Our Products & Services
          </h1>
          <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto">
            Comprehensive Coke Solutions for Industrial Excellence
          </p>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="coke" className="w-full">
            <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-3 mb-12 h-auto">
              <TabsTrigger
                value="coke"
                className="py-3 data-[state=active]:bg-[#C0D8A3] data-[state=active]:text-white"
              >
                <Package className="h-4 w-4 mr-2" />
                Metallurgical Coke
              </TabsTrigger>
              <TabsTrigger
                value="trading"
                className="py-3 data-[state=active]:bg-[#C0D8A3] data-[state=active]:text-white"
              >
                <TrendingUp className="h-4 w-4 mr-2" />
                Green Energy Trading
              </TabsTrigger>
              <TabsTrigger
                value="future"
                className="py-3 data-[state=active]:bg-[#C0D8A3] data-[state=active]:text-white"
              >
                <Rocket className="h-4 w-4 mr-2" />
                Future Offerings
              </TabsTrigger>
            </TabsList>

            {/* Tab 1: Metallurgical Coke */}
            <TabsContent value="coke" className="space-y-8">
              <div className="max-w-5xl mx-auto">
                <p className="text-lg text-[#2C3E50] mb-8">
                  Our Metallurgical Coke products are designed to meet the
                  exacting standards of India's steel, ferro-alloy, and foundry
                  industries. We specialize in low ash and low phosphorus grades
                  that enhance operational efficiency and product quality.
                </p>

                {/* Product Grades Table */}
                <Card className="mb-8 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-[#1B4D40] text-white">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold">
                              Grade
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">
                              Size
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">
                              Ash %
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">
                              Phosphorus %
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">
                              Sulfur %
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold">
                              Application
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {cokeGrades.map((grade, index) => (
                            <tr
                              key={index}
                              className="hover:bg-[#F5F1E8] transition-colors"
                            >
                              <td className="px-4 py-3 text-sm font-medium text-[#1B4D40]">
                                {grade.grade}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#2C3E50]">
                                {grade.size}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#2C3E50]">
                                {grade.ash}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#2C3E50]">
                                {grade.phosphorus}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#2C3E50]">
                                {grade.sulfur}
                              </td>
                              <td className="px-4 py-3 text-sm text-[#2C3E50]">
                                {grade.application}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Key Specifications */}
                <div className="bg-[#F5F1E8] rounded-lg p-8 mb-8">
                  <h3 className="text-2xl font-bold text-[#1B4D40] mb-6">
                    Key Specifications
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-semibold text-[#1B4D40]">
                            Fixed Carbon:
                          </span>
                          <span className="text-[#2C3E50] ml-2">82% - 88%</span>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-semibold text-[#1B4D40]">
                            Volatile Matter:
                          </span>
                          <span className="text-[#2C3E50] ml-2">1.5% - 3%</span>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-semibold text-[#1B4D40]">
                            Moisture:
                          </span>
                          <span className="text-[#2C3E50] ml-2">5% - 8%</span>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-semibold text-[#1B4D40]">
                            CRI/CSR:
                          </span>
                          <span className="text-[#2C3E50] ml-2">
                            Available on request
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quality Assurance */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-[#1B4D40] mb-4">
                    Quality Assurance
                  </h3>
                  <p className="text-[#2C3E50] leading-relaxed">
                    All our Metallurgical Coke products undergo rigorous quality
                    testing to ensure they meet specified standards. We are
                    implementing ISO 9001:2015 quality management systems and
                    establishing state-of-the-art testing facilities to provide
                    complete product traceability and certification.
                  </p>
                </div>

                <div className="text-center">
                  <Button className="bg-[#C0D8A3] hover:bg-[#27ae60] text-white">
                    <Download className="h-4 w-4 mr-2" />
                    Download Technical Specification PDF
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Tab 2: Energy Trading */}
            <TabsContent value="trading" className="space-y-8">
              <div className="max-w-5xl mx-auto">
                <p className="text-lg text-[#2C3E50] mb-8">
                  Our Green Energy trading division provides comprehensive
                  Metallurgical Coke procurement and supply chain solutions. We
                  leverage deep industry relationships and market expertise to
                  deliver reliable, cost-effective services to our clients.
                </p>

                {/* Trading Services Grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-12">
                  {tradingServices.map((service, index) => (
                    <Card
                      key={index}
                      className="border-2 hover:border-[#C0D8A3] transition-all"
                    >
                      <CardContent className="p-6">
                        <h3 className="text-xl font-bold text-[#1B4D40] mb-4">
                          {service.title}
                        </h3>
                        <ul className="space-y-2">
                          {service.points.map((point, idx) => (
                            <li
                              key={idx}
                              className="flex items-start space-x-2"
                            >
                              <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                              <span className="text-[#2C3E50]">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Industries Served */}
                <div className="bg-[#F5F1E8] rounded-lg p-8 mb-8">
                  <h3 className="text-2xl font-bold text-[#1B4D40] mb-4">
                    Industries Served
                  </h3>
                  <div className="flex flex-wrap gap-4">
                    <span className="px-4 py-2 bg-white rounded-lg text-[#2C3E50] font-medium">
                      Steel Manufacturing
                    </span>
                    <span className="px-4 py-2 bg-white rounded-lg text-[#2C3E50] font-medium">
                      Ferro-Alloy Production
                    </span>
                    <span className="px-4 py-2 bg-white rounded-lg text-[#2C3E50] font-medium">
                      Foundries
                    </span>
                  </div>
                </div>

                <div className="text-center">
                  <Button
                    asChild
                    className="bg-[#C0D8A3] hover:bg-[#27ae60] text-white"
                  >
                    <Link to="/contact">Discuss Your Trading Needs</Link>
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Tab 3: Future Offerings */}
            <TabsContent value="future" className="space-y-8">
              <div className="max-w-4xl mx-auto text-center">
                <div className="bg-[#F5F1E8] rounded-lg p-8 mb-8">
                  <h3 className="text-2xl font-bold text-[#1B4D40] mb-4">
                    Coming Soon - Commissioning Phase
                  </h3>
                  <p className="text-lg text-[#2C3E50] mb-8">
                    We are actively developing our manufacturing capabilities to
                    deliver direct Metallurgical Coke production and expanded
                    Green Energy solutions.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-12">
                  <Card className="border-2">
                    <CardContent className="p-8">
                      <h3 className="text-xl font-bold text-[#1B4D40] mb-4">
                        Direct Metallurgical Coke Manufacturing
                      </h3>
                      <ul className="space-y-3 text-left">
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                          <span className="text-[#2C3E50]">
                            Capacity: 100,000 MTPA (planned)
                          </span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                          <span className="text-[#2C3E50]">
                            Location: Maharashtra, India
                          </span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                          <span className="text-[#2C3E50]">
                            Expected Timeline: Q4 2025
                          </span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-2">
                    <CardContent className="p-8">
                      <h3 className="text-xl font-bold text-[#1B4D40] mb-4">
                        Green Energy Solutions (Phase 2)
                      </h3>
                      <ul className="space-y-3 text-left">
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                          <span className="text-[#2C3E50]">
                            Additional product development
                          </span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                          <span className="text-[#2C3E50]">
                            Sustainability initiatives
                          </span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                          <span className="text-[#2C3E50]">
                            Advanced solutions for industry
                          </span>
                        </li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <Button
                  asChild
                  className="bg-[#C0D8A3] hover:bg-[#27ae60] text-white"
                >
                  <Link to="/">View Commissioning Timeline</Link>
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Quality Standards Section */}
      <section className="bg-[#F5F1E8] py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B4D40] mb-8 text-center">
              Quality Standards & Certifications
            </h2>
            <p className="text-center text-[#2C3E50] mb-8">
              We are actively pursuing all necessary certifications to ensure
              our operations meet the highest industry standards.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-[#1B4D40] mb-4">
                    Current Certifications (Under Development)
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                      <span className="text-[#2C3E50]">
                        ISO 9001:2015 Planning
                      </span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                      <span className="text-[#2C3E50]">
                        Environmental Compliance In Progress
                      </span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle className="h-5 w-5 text-[#C0D8A3] flex-shrink-0 mt-0.5" />
                      <span className="text-[#2C3E50]">
                        Industry Standards Development
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-[#1B4D40] mb-4">
                    Our Commitment
                  </h3>
                  <p className="text-[#2C3E50]">
                    All certifications will be in place before commercial
                    operations commence. We are committed to meeting
                    international quality standards and maintaining full
                    regulatory compliance from day one.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#C0D8A3] py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Source Your Metallurgical Coke?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white text-[#C0D8A3] hover:bg-gray-100 font-medium px-8"
            >
              <Link to="/contact">Request Product Information</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#C0D8A3] font-medium px-8"
            >
              <Link to="/contact">Discuss Trading Needs</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;